import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { useGetSlotAllocations, useGetAllocatedSlots } from "../../queries/advertisement";
import { useMemo } from "react";
import Chip from "../../components/Chip";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import TanStackTable from "../../components/TanStackTable";
import { FaTrash } from "react-icons/fa";

const SlotAllocationList = () => {
    const navigate = useNavigate();

    const { data, isPending } = useGetAllocatedSlots();
    // const { mutateAsync: deallocateSlot, isPending: isDeallocating } = useGetAllocatedSlots();

    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('advertisementSlot.name', {
            header: 'Slot Name',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.row.original?.advertisementSlot?.name || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor('advertisementSlot.location', {
            header: 'Slot Location',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.row.original?.advertisementSlot?.location || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor('advertisement.name', {
            header: 'Ad Name',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.row.original?.advertisement?.name || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor('client', {
            header: 'Client',
            cell: info => {
                const row = info.row.original;
                const sponsorType = row?.sponsorType;
                const companyName = row?.companyId?.companyName;
                const ngoName = row?.NGOId?.ngoName || row?.NGOId?.name; // Handle different NGO name fields
                
                const clientName = sponsorType === 'company' ? companyName : ngoName;
                const clientType = sponsorType === 'company' ? 'Company' : 'NGO';
                
                return (
                    <div className="whitespace-normal break-words">
                        <div className="font-medium line-clamp-1">
                            {clientName || 'N/A'}
                        </div>
                        <div className="text-sm text-secondary capitalize">
                            {clientType}
                        </div>
                    </div>
                );
            },
        }),
        columnHelper.accessor('title', {
            header: 'Campaign Title',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.getValue() || 'N/A'}
                </div>
            ),
        }),

        columnHelper.accessor('endDate', {
            header: 'Campaign End Date',
            align: 'center',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-1">
                    {info.getValue() ? new Date(info.getValue()).toRegularDate('en-GB') : 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor('active', {
            header: () => <div className="text-center">Status</div>,
            size: 10,
            align: 'center',
            cell: info => {
                const active = info.getValue();
                const isExpired = info.row.original?.endDate && 
                    new Date(info.row.original.endDate) < new Date();
                
                let displayStatus, color;
                if (isExpired) {
                    displayStatus = 'Expired';
                    color = 'red';
                } else if (active) {
                    displayStatus = 'Active';
                    color = 'green';
                } else {
                    displayStatus = 'Inactive';
                    color = 'gray';
                }
                
                return <Chip value={displayStatus} color={color} />;
            },
        }),
        // columnHelper.accessor('actions', {
        //     header: () => <div className="text-center">Actions</div>,
        //     enableColumnFilter: false,
        //     cell: info => {
        //         return (
        //             <div className='flex items-center justify-center'>
        //                 <button
        //                     className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
        //                     onClick={() => {
        //                         // handleDeallocate(info.row.original._id);
        //                     }}
        //                     title="Deallocate Slot"
        //                 >
        //                     <FaTrash className="fill-chipRed" />
        //                 </button>
        //             </div>
        //         )
        //     },
        // }),
    ], []);

    // const handleDeallocate = async (id) => {
    //     const confirm = window.confirm("Are you sure you want to deallocate this slot?");
    //     if (!confirm) return;
    //     deallocateSlot(id, {
    //         onSuccess: () => {
    //             toast.success('Slot deallocated successfully');
    //         },
    //         onError: (error) => {
    //             toast.error(`Error deallocating slot: ${error.message || 'Unknown error'}`);
    //         },
    //     });
    // }

    if (isPending) {
        return (
            <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
                <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                    <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
                        <h1 className="self-start text-lg font-semibold">Slot Allocation List</h1>
                        <p>Loading...</p>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <div>
            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full max-md:max-w-full">
                    <div className="flex justify-between items-center pl-6 pb-4">
                        <h1 className="text-lg font-bold">Slot Allocation List</h1>
                        <button
                            onClick={() => navigate('/advertisements/allocate-slot')}
                            className="mr-6 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            Allocate New Slot
                        </button>
                    </div>
                    <div className="pl-5">
                        <TanStackTable
                            data={data || []}
                            columns={columns}
                            enablePagination={true}
                        />
                    </div>
                </div>
            </section>
            <ToastContainer
                position="top-right"
                autoClose={3000}
            />
        </div>
    );
}

export default SlotAllocationList;
